import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MessageCircle, PenLine, RotateCcw } from 'lucide-react';

import CtaLink from '@/components/CtaLink';
import { useLanguage } from '@/components/LanguageContext';
import { withCta } from '@/lib/ctaTracking';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import LogosMarquee from '@/components/landing/v3/LogosMarquee';
import MapaSeoComparador from '@/components/landing/v3/MapaSeoComparador';
import PricingTableV3 from '@/components/landing/v3/PricingTableV3';
import RankingHeroMock from '@/components/landing/v3/RankingHeroMock';
import SectionKicker from '@/components/landing/v3/SectionKicker';
import ComoFuncionaV3 from '@/components/landing/v3/ComoFuncionaV3';
import PuntuacionSeoV3 from '@/components/landing/v3/PuntuacionSeoV3';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * HomeV3 — la home del rediseño, portada desde `MyTapstar Home.dc.html`.
 *
 * Este fichero es el ÍNDICE de la página: monta las secciones en orden y les
 * pasa su trozo de copy. Todo lo que tiene estado o maqueta compleja vive en
 * `components/landing/v3/`. La home de la V2 son 698 líneas con cuatro mocks
 * animados dentro y es imposible localizar una sección; esta se lee de un
 * vistazo.
 */
const LOGO_LANZADERA = '/landing-images/v3/respaldo-lanzadera.png';

const ICONOS_FUNCION = {
  respuestas: MessageCircle,
  antiguas: RotateCcw,
  programar: Calendar,
  perfil: PenLine,
};

export default function HomeV3() {
  const { language } = useLanguage();
  const c = pickLandingV3Content(language).home;

  return (
    <LandingShellV3>
      <Hero c={c.hero} />
      <Respaldo c={c.respaldo} />
      <ComoFuncionaV3 c={c.como} />
      <MapaSeoComparador c={c.mapa} />
      <PuntuacionSeoV3 c={c.seo} />
      <Funciones c={c.funciones} />
      <PricingTableV3 />
      <Cifras c={c.cifras} />
      <CtaFinalOscuro
        tituloA={c.cierre.tituloA}
        acento={c.cierre.acento}
        cta={c.cierre.cta}
        microcopy={c.cierre.microcopy}
        enterpriseA={c.cierre.enterpriseA}
        enterpriseCta={c.cierre.enterpriseCta}
        ctaTrial="landing.v3.cierre.trial"
        ctaVentas="landing.v3.cierre.ventas"
      />
    </LandingShellV3>
  );
}

function Hero({ c }) {
  return (
    <section className="bg-white px-6 pb-6">
      <div className="max-w-[1180px] mx-auto flex flex-wrap items-center gap-8 lg:gap-14 pt-4 pb-8">
        <div className="flex-1 basis-[420px] min-w-0">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-[7px] shadow-[0_1px_3px_rgba(0,0,0,.06)]">
            <span className="w-[7px] h-[7px] rounded-full bg-v3-green block" aria-hidden="true" />
            <span className="text-[13px] font-medium text-v3-body">{c.badge}</span>
          </div>

          {/* El diseño mete dos `<br>` para partir el titular en tres líneas
              exactas. Aquí no: a 375 px esos saltos dejan líneas huérfanas de
              dos palabras. Se deja que el texto fluya y `max-w` hace el resto. */}
          <h1 className="text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.08] tracking-[-.03em] font-bold mt-5 text-v3-ink">
            {c.tituloA}
            <span className="text-v3-gold">{c.acento1}</span>
            {c.tituloB}
            <span className="text-v3-gold">{c.acento2}</span>
          </h1>

          <p className="text-[17px] lg:text-[18px] leading-[1.6] text-v3-body mt-5 max-w-[50ch]">
            {c.subtitulo}
          </p>

          <div className="flex gap-3 mt-7 flex-wrap">
            <CtaLink
              as={Link}
              cta="landing.v3.hero.trial"
              to={withCta('/register', 'landing.v3.hero.trial')}
              className="inline-flex items-center bg-v3-gold hover:bg-v3-golddeep text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
            >
              {c.ctaPrimario}
            </CtaLink>
            <CtaLink
              as="a"
              cta="landing.v3.hero.como"
              href="#como-funciona"
              className="inline-flex items-center gap-2 bg-v3-sunken hover:bg-v3-track text-v3-ink text-base font-semibold rounded-full px-[26px] py-[15px] whitespace-nowrap transition-colors"
            >
              {c.ctaSecundario}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </CtaLink>
          </div>
        </div>

        <div className="flex-1 basis-[420px] min-w-0 w-full">
          <RankingHeroMock c={c} />
        </div>
      </div>
    </section>
  );
}

function Respaldo({ c }) {
  return (
    <section className="bg-white px-6 pt-2 pb-4">
      <div className="max-w-[1180px] mx-auto flex items-center justify-center gap-5 flex-wrap">
        <span className="text-[13px] font-semibold text-v3-muted tracking-[-.01em]">
          {c.titulo}
        </span>
        <img src={LOGO_LANZADERA} alt={c.alt} className="h-[22px] w-auto block" loading="lazy" />
      </div>
    </section>
  );
}

function Funciones({ c }) {
  return (
    <section id="funciones" className="max-w-[1180px] mx-auto px-6 pt-16 md:pt-[76px] pb-16 md:pb-[76px]">
      <SectionKicker>{c.kicker}</SectionKicker>

      <div className="grid gap-5 mt-7 sm:grid-cols-2 lg:grid-cols-4">
        {c.items.map((f) => {
          const Icono = ICONOS_FUNCION[f.clave];
          return (
            <div
              key={f.clave}
              className="bg-v3-surface rounded-[20px] p-[26px] flex flex-col gap-4"
            >
              <div className="flex items-center justify-between gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-white v3-shadow flex items-center justify-center">
                  <Icono className="w-5 h-5 text-v3-golddeep" aria-hidden="true" />
                </div>
                <span className="text-[11px] font-semibold text-v3-body bg-v3-track rounded-full px-2.5 py-1 whitespace-nowrap">
                  {f.tag}
                </span>
              </div>
              <div className="text-[18px] font-bold tracking-[-.02em] leading-[1.3] text-v3-ink">
                {f.titulo}
              </div>
              <div className="text-[14.5px] leading-[1.6] text-v3-body">{f.desc}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Cifras({ c }) {
  return (
    <section className="max-w-[1180px] mx-auto px-6 py-16 md:py-[72px]">
      <div className="grid gap-5 items-center md:grid-cols-3">
        <h2 className="text-[24px] md:text-[26px] leading-[1.2] tracking-[-.02em] font-bold max-w-[26ch] text-v3-ink">
          {c.titulo}
        </h2>
        {c.items.map((x) => (
          <div key={x.valor} className="bg-v3-surface rounded-[20px] p-[30px]">
            <div className="text-[36px] md:text-[42px] font-bold tracking-[-.03em] leading-none text-v3-golddeep">
              {x.valor}
            </div>
            <div className="text-[15px] text-v3-body mt-2.5 leading-[1.5]">{x.etiqueta}</div>
          </div>
        ))}
      </div>

      <div className="mt-11">
        <LogosMarquee titulo={c.logosTitulo} />
      </div>
    </section>
  );
}
